import { get } from './apiService';

export interface Locale {
  id: string;
  type: string;
  name?: string;
  city?: string;
  country?: string;
  stateProvince?: string;
  postalCode?: string;
  lat?: number;
  lon?: number;
  radius?: number;
  /**
   * Locale-wide cap on listing audience reach. 1 = friends & family,
   * 2 = friends of friends, 3 = anyone. The BE applies this server-side
   * via `LEAST(listing.share_connection_level, locale.max_share_connection_level)`
   * so a chef's per-listing or per-profile choice that exceeds this cap
   * still has the cap as the effective reach. The FE uses it to constrain
   * the audience pickers (`ShareLevelPicker`, `ShareLevelDropdown`,
   * `FirstPublishVisibilityModal`) so chefs don't see options that won't
   * survive the cap.
   */
  maxShareConnectionLevel?: number;
  /**
   * Locale-wide cap on listing price, in CENTS (matches `Listing.price`).
   * 0 = **Free-only**: chefs in this locale may only publish free dishes.
   * The FE uses it to constrain the publish-screen price wheel so chefs
   * can't pick a price above what the area allows (e.g. 1200 = "$12 max").
   * Not yet enforced server-side, so the FE also keeps a publish-time
   * safety block that should never fire once the wheel is constrained.
   */
  maxPrice?: number;
}

export interface GetLocaleParams {
  lat?: number;
  lon?: number;
  postalCode?: string;
}

export async function getLocale(params: GetLocaleParams): Promise<Locale> {
  const qs = new URLSearchParams();
  // Send all known signals — the BE uses lat/lon for spatial-first dish-locale
  // lookups and falls back to the explicit postalCode when its own reverse-geocode
  // doesn't return one (Google occasionally omits `postal_code` for some lat/lons,
  // which previously made the create-branch 404 with "could not determine postal
  // code from location"). The FE knows the postal code from the forward geocode
  // — no point throwing it away.
  if (params.lat != null && params.lon != null) {
    qs.set('lat', String(params.lat));
    qs.set('lon', String(params.lon));
  }
  if (params.postalCode) {
    qs.set('postalCode', params.postalCode);
  }
  return get<Locale>(`/locale?${qs}`);
}

/**
 * Fetch a locale by ID. Used by the auth-restore flow to rehydrate the
 * user's `currentLocaleId` without going through a resolve path. The BE's
 * `/locale/{id}` deliberately skips the silent `currentLocaleId` patch
 * that the query-string variants do (which would be a redundant write).
 */
export async function getLocaleById(id: string): Promise<Locale> {
  return get<Locale>(`/locale/${id}`);
}
